// Central API client for the Gurukul Enterprise backend.
//
// Ported from v1 essentially unchanged — the services layer was the healthiest
// part of that codebase and its hard-won production fixes (the ModSecurity
// Content-Type workaround below, in particular) are not worth rediscovering.

import { endpointShape, track, type RequestFailureReason } from '@/lib/analytics';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message: string;
}

/** Paginated list envelope. Laravel puts the page info alongside `data`. */
export interface ApiListResponse<T = unknown> extends ApiResponse<T[]> {
  meta: {
    current_page: number;
    from?: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly errors?: Record<string, string[]>;

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
  }

  /** Laravel validation errors, flattened for display beside a field. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0];
  }
}

/** Called on 401 so the app can clear its own state instead of a hard reload. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('auth_token');

  const method = (options.method || 'GET').toUpperCase();
  const isFormData = options.body instanceof FormData;
  // GET/HEAD/DELETE carry no body. Sending Content-Type on a bodyless request
  // trips ModSecurity in production (406), even though the header is harmless
  // to the app. Only advertise a body type when there's actually a body.
  const isBodylessMethod = method === 'GET' || method === 'HEAD' || method === 'DELETE';

  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Skip Content-Type for FormData (browser sets the multipart boundary)
    // and for bodyless methods (avoids the ModSecurity 406 above).
    ...(isFormData || isBodylessMethod ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options.headers as Record<string, string>) || {}),
  };

  // Guard against a caller merging Content-Type back in for a bodyless request.
  if (isBodylessMethod) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }

  /* Which requests fail, where, and how often — the most useful non-money
     signal the app produces. The path is templated so ids never reach the
     property and every failure of the same call counts as one endpoint.

     Every way out of this function that is not a response the caller can use
     goes through here. Emitting only on `!response.ok` — which is what this
     did — counted the failures the server was healthy enough to describe and
     silently dropped the three that matter most on a school's connection: a
     dropped line, an expired session, and a gateway returning HTML. `reason`
     is what separates them; v1's events carry no `reason`, so a report that
     filters on it is reading ui-v2 only. */
  const fail = (status: number, reason: RequestFailureReason) =>
    track('Request Failed', { endpoint: endpointShape(endpoint), method, status, reason });

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  } catch {
    /* The line dropped, DNS failed, or the browser refused the request outright:
       `fetch` rejects and there is no status to report. 0 is the conventional
       stand-in and keeps `status` a number for charting. This used to escape as
       a raw `TypeError`, which reached the user as "Failed to fetch". */
    fail(0, 'network');
    throw new ApiError('Could not reach the server. Check the connection and try again.', 0);
  }

  if (response.status === 401) {
    /* Counted before the handler runs: signing the user out unmounts the screen
       that made the call, and an expired session mid-task is a support question
       in its own right — "it logged me out again" needs a number behind it. */
    fail(401, 'unauthorized');
    onUnauthorized?.();
    throw new ApiError('Your session has expired. Sign in to continue.', 401);
  }

  let json: {
    success?: boolean;
    message?: string;
    errors?: Record<string, string[]>;
  };
  try {
    json = (await response.json()) as typeof json;
  } catch {
    /* The body was not JSON. In production that is ModSecurity or a gateway
       answering with an HTML error page — the 406 the Content-Type workaround
       above exists for is exactly this shape. It threw a `SyntaxError` from the
       parser before anything counted it, so the one failure mode this client
       has real production history with was the one it could not report. */
    fail(response.status, 'malformed');
    throw new ApiError(`Request failed with status ${response.status}`, response.status);
  }

  if (!response.ok || !json.success) {
    fail(response.status, 'http');
    throw new ApiError(
      json.message || `Request failed with status ${response.status}`,
      response.status,
      json.errors,
    );
  }

  return json as ApiResponse<T>;
}

/** Same as `apiRequest`, but returns the paginated envelope with `meta`. */
export async function apiList<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiListResponse<T>> {
  return (await apiRequest<T[]>(endpoint, options)) as ApiListResponse<T>;
}

/** Builds a query string, dropping empty values so no `?name=` reaches the API. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
